/** Binding the key layer to the document (UX §9.1).
 *
 * The scope rule is what makes single-character shortcuts safe here:
 *
 * > A single key fires **only** when focus is on the document body or on the stage container
 * > (`[data-live-stage]`). Inside any control — a button, a field, a select, a card in the
 * > deck, a dialog — the browser's own handling wins and this layer stands down.
 *
 * Everything else falls out of that for free: typing in the ask-aloud note never asks a
 * question; Space on a focused deck button activates that button, which is the same action;
 * Space on a focused "Ask again" activates it, deliberately, because the teacher tabbed there.
 * `Escape` always returns focus to the stage, which is how the layer is got back.
 *
 * Auto-repeat is ignored (`event.repeat`): one press, one action.
 */
import { useEffect, useRef } from "react";

import { resolve, type KeyContext, type LiveAction } from "./shortcuts";

type Options = {
  enabled: boolean;
  context: () => KeyContext;
  onAction: (action: LiveAction) => void;
  onRefuse: (messageKey: string) => void;
};

/** Is focus somewhere the global layer may act? */
export function inScope(active: Element | null): boolean {
  if (!active || active === document.body) return true;
  if (active instanceof HTMLElement && active.hasAttribute("data-live-stage")) return true;
  return false;
}

export function useLiveShortcuts({ enabled, context, onAction, onRefuse }: Options): void {
  const ctx = useRef(context);
  ctx.current = context;
  const act = useRef(onAction);
  act.current = onAction;
  const refuse = useRef(onRefuse);
  refuse.current = onRefuse;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return; // a held key is one press, not forty
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Escape is the way back to the stage and works from anywhere except a dialog, which
      // handles its own and stops the event before it reaches here.
      if (!inScope(document.activeElement) && e.key !== "Escape") return;
      const r = resolve(e.key, ctx.current());
      if (!r) return;
      e.preventDefault();
      if ("refuse" in r) refuse.current(r.refuse);
      else act.current(r.action);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}

/** Put focus back on the stage, so the global layer applies again. */
export function focusStage(): void {
  document.querySelector<HTMLElement>("[data-live-stage]")?.focus();
}
